import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import gsap from 'gsap';
import { useAppDispatch } from '../../../../app/redux/hooks';
import { updateToast } from '../../../../common/redux/reducer/commonSlices/toastSlice';

interface Load {
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  color: number;
}

interface Container {
  width: number;
  height: number;
  depth: number;
}

interface Props {
  loads: Load[];
  container: Container;
}

const Custom3dContainer = ({ loads, container }: Props) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const [size, setSize] = useState('');
  const dispatch = useAppDispatch();
  const isLoadValid = (load: Load) =>
    load.x >= 0 &&
    load.x + load.width <= container.width &&
    load.y >= 0 &&
    load.y + load.height <= container.height &&
    load.z >= 0 &&
    load.z + load.depth <= container.depth;

  const sanitizeSceneNames = (scene: THREE.Scene) => {
    scene.traverse((obj: any) => {
      // Object name check
      if (obj.name == null || typeof obj.name !== 'string') {
        obj.name = '';
      }

      // Material name check
      if (obj.material) {
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach((mat: any) => {
          if (mat.name == null || typeof mat.name !== 'string') {
            mat.name = '';
          }

          if (mat.map && (mat.map.name == null || typeof mat.map.name !== 'string')) {
            mat.map.name = '';
          }
        });
      }
    });
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);
    let isReady = false;
    let camera: any = null;
    if (size === 'box') {
      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 60000);
    } else {
      camera = new THREE.PerspectiveCamera(75, width / height, 5, 3000);
    }

    const loadGroup = new THREE.Group();
    loadGroup.clear();
    size === 'extralarge' && scene.add(loadGroup);

    if (size === 'extralarge') {
      camera.position.set(
        container.width * 2.5,
        container.height * 2.5,
        container.depth * 1.5
      );
    } else if (size === 'large') {
      camera.position.set(
        container.width * 1.5,
        container.height * 1.2,
        container.depth * 1.5
      );
    } else if (size === 'box') {
      camera.position.set(
        container.width * 2.2,
        container.height * 2.1,
        container.depth * 1.2
      );
    } else {
      camera.position.set(
        container.width * 2.5,
        container.height * 2,
        container.depth * 2.5
      );
    }

    const center = new THREE.Vector3(
      container.width / 2,
      container.height / 2,
      container.depth / 2
    );
    camera.lookAt(center);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.minDistance = Math.max(container.width, container.depth) * 1.5;
    controls.maxDistance = Math.max(container.width, container.depth) * 5;
    controls.target.copy(center);

    // 👇 Allow only top, left, right views
    controls.minPolarAngle = 0; // from top
    controls.maxPolarAngle = Math.PI / 2; // up to horizon only

    controls.update();

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(20, 20, 20);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    if (loads.length > 0) {
      const containerBox = new THREE.BoxGeometry(
        container.width,
        container.height,
        container.depth
      );
      const edges = new THREE.EdgesGeometry(containerBox);
      const line = new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: 0x000000 })
      );
      line.position.set(container.width / 2, container.height / 2, container.depth / 2);
      // scene.add(line);

      // Flatbed

      // Flatbed
      if (size === 'extralarge') {
        const flatbedGeometry = new THREE.BoxGeometry(
          container.width,
          2,
          container.depth
        );
        const flatbedMaterial = new THREE.MeshLambertMaterial({ color: 0x555555 });
        const flatbed = new THREE.Mesh(flatbedGeometry, flatbedMaterial);
        flatbed.position.set(container.width / 2, 0.5, container.depth / 2);
        // scene.add(flatbed);

        loadGroup.add(flatbed);
      }
      size === 'extralarge' ? loadGroup.add(line) : scene.add(line);

      loads.forEach(load => {
        if (!isLoadValid(load)) {
          return;
        }

        const geometry = new THREE.BoxGeometry(load.width, load.height, load.depth);
        const material = new THREE.MeshLambertMaterial({
          color: load.color,
          transparent: true,
          opacity: 0.8
        });
        const mesh = new THREE.Mesh(geometry, material);

        // Set initial Y position above the scene
        mesh.position.set(
          load.x + load.width / 2,
          load.y + load.height / 2 + 100, // offset from top
          load.z + load.depth / 2
        );
        // scene.add(mesh);

        size === 'extralarge' ? loadGroup.add(mesh) : scene.add(mesh);

        size === 'extralarge' && loadGroup.scale.set(1, 1, 0.6); // Makes everything smaller (zoom in effect)

        // Animate drop from top
        gsap.to(mesh.position, {
          y: load.y + load.height / 2,
          duration: 1,
          ease: 'bounce.out'
        });

        // Add wireframe
        const wireframe = new THREE.WireframeGeometry(geometry);
        const outline = new THREE.LineSegments(
          wireframe,
          new THREE.LineBasicMaterial({ color: 0x000000 })
        );
        outline.position.copy(mesh.position);
        size === 'extralarge' ? loadGroup.add(outline) : scene.add(mesh);

        // Animate wireframe too
        gsap.to(outline.position, {
          y: load.y + load.height / 2,
          duration: 1,
          ease: 'bounce.out'
        });
      });

      const loader = new GLTFLoader();
      if (size === 'medium') {
        loader.load(
          '/models/truck/truck.glb',
          gltf => {
            const truck = gltf.scene;

            const box = new THREE.Box3().setFromObject(truck);
            const size = new THREE.Vector3();
            // const center = new THREE.Vector3();
            box.getSize(size);
            // box.getCenter(center);

            // truck.position.sub(center); // move to origin

            const scaleX = container.width / size.x;
            const scaleY = container.height / size.y;
            const scaleZ = container.depth / size.z;
            const uniformScale = Math.min(scaleX, scaleY, scaleZ);
            // truck.scale.setScalar(uniformScale * 2.7);
            truck.scale.set(scaleX * 1.5, scaleY * 1.4, scaleZ * 1.7);

            truck.position.set(
              container.width / 2,
              container.height / 2 - container.height - 5,
              container.depth / 2 - 108
            );

            truck.rotation.y = Math.PI;

            scene.add(truck);
          },
          xhr => {
            if (xhr.total) {
            }
          },
          error => {}
        );
      } else if (size === 'small') {
        loader.load(
          '/models/truck/bike.glb',
          gltf => {
            const truck = gltf.scene;

            const box = new THREE.Box3().setFromObject(truck);
            const size = new THREE.Vector3();
            // const center = new THREE.Vector3();
            box.getSize(size);

            const scaleX = container.width / size.x;
            const scaleY = container.height / size.y;
            const scaleZ = container.depth / size.z;
            const uniformScale = Math.min(scaleX, scaleY, scaleZ);
            truck.scale.setScalar(uniformScale * 3.7);

            truck.position.set(
              container.width / 2 - container.width - 100,
              container.height / 2 - 2 * container.height - 60,
              container.depth / 6 - container.depth / 10 + 1
            );
            truck.rotation.y = Math.PI / 2;

            scene.add(truck);
          },
          xhr => {
            if (xhr.total) {
            }
          },
          error => {}
        );
      } else if (size === 'large') {
        loader.load(
          '/models/truck/heavyTruck4.glb',
          gltf => {
            const truck = gltf.scene;

            const box = new THREE.Box3().setFromObject(truck);
            const size = new THREE.Vector3();
            const center = new THREE.Vector3();
            box.getSize(size);
            box.getCenter(center);

            // truck.position.sub(center); // move to origin

            const scaleX = container.width / size.x;
            const scaleY = container.height / size.y;
            const scaleZ = container.depth / size.z;
            const uniformScale = Math.min(scaleX, scaleY, scaleZ);
            // truck.scale.setScalar(uniformScale * 1.7);
            truck.scale.set(scaleX * 1.6, scaleY * 1.7, scaleZ * 1.7);

            truck.position.set(
              container.width / 2,
              container.height / 2 - container.height - 20,
              container.depth / 2 - 70
            );
            truck.rotation.y = Math.PI;

            scene.add(truck);
          },
          xhr => {
            if (xhr.total) {
            }
          },
          error => {}
        );
      } else if (size === 'extralarge') {
        loader.load(
          '/models/truck/containerTruck1.glb',
          gltf => {
            const truck = gltf.scene;

            const box = new THREE.Box3().setFromObject(truck);
            const size = new THREE.Vector3();
            box.getSize(size);

            const scaleX = container.width / size.x;
            const scaleY = container.height / size.y;
            const scaleZ = container.depth / size.z;
            const uniformScale = Math.min(scaleX, scaleY, scaleZ);
            truck.scale.set(scaleX * 1.2, scaleY * 1.7, scaleZ * 1);

            truck.position.set(
              container.width / 2,
              container.height - container.height + 15,
              container.depth / 2 - container.depth / 2 + 100
            );
            truck.rotation.y = Math.PI;

            scene.add(truck);
          },
          xhr => {
            if (xhr.total) {
            }
          },
          error => {}
        );
      }
    } else {
      const loader = new GLTFLoader();
      loader.load(
        '/models/truck/box7.glb',
        gltf => {
          const truck = gltf.scene;

          // First, compute bounding box
          const box = new THREE.Box3().setFromObject(truck);
          const size = new THREE.Vector3();
          box.getSize(size);

          // Scale the truck
          const scaleX = container.width / size.x;
          const scaleY = container.height / size.y;
          const scaleZ = container.depth / size.z;
          truck.scale.set(scaleX * 1, scaleY * 1.4, scaleZ * 1);

          // Update bounding box and center again after scaling
          box.setFromObject(truck);
          const center = new THREE.Vector3();
          box.getCenter(center);

          // Recenter the truck to origin
          truck.position.sub(center);

          // Wrap in group to position globally
          const wrapper = new THREE.Group();
          wrapper.add(truck);

          // Center the wrapper in the container
          wrapper.position.set(
            container.width / 2,
            container.height / 2,
            container.depth / 2
          );

          scene.add(wrapper);
          isReady = true;
        },
        xhr => {},
        error => {}
      );
    }

    const animate = () => {
      // requestAnimationFrame(animate);
      animationRef.current = requestAnimationFrame(animate);
      controls?.update();
      renderer?.render(scene, camera);
    };
    animate();

    // return () => {
    //   if (animationRef.current) cancelAnimationFrame(animationRef.current);
    //   mount.removeChild(renderer.domElement);
    //   renderer.dispose();
    //   controls.dispose();
    // };
    return () => {
      // Stop animation frame
      if (animationRef.current) cancelAnimationFrame(animationRef.current);

      // Remove renderer DOM element
      if (renderer && renderer.domElement && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }

      // Dispose renderer and force context loss
      if (renderer) {
        renderer.dispose();

        // Force WebGL context loss
        const gl = renderer.getContext();
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext(); // This really forces cleanup
      }

      // Dispose OrbitControls
      if (controls) {
        controls.dispose();
      }

      // Traverse and dispose all scene children
      scene.traverse(child => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) child.geometry.dispose();

          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else if (child.material) {
            child.material.dispose();
          }
        }
      });
    };
  }, [size]);

  useEffect(() => {
    const { depth, width, height } = container;
    if (loads.length > 0) {
      if (depth < 230 && width < 85 && height < 90) {
        setSize('small');
      } else if (depth <= 312 && width <= 96 && height <= 120) {
        setSize('medium');
      } else if (depth <= 636 && width <= 102 && height <= 144) {
        setSize('large');
      } else if (depth > 636 && width <= 110 && height <= 160) {
        setSize('extralarge');
      } else {
        setSize('no size found check the container dimensions');
        dispatch(
          updateToast({
            show: 'true',
            message: 'Truck Image Size Not Available.',
            severity: 'warning'
          })
        );
      }
    } else {
      setSize('box');
    }
  }, [container, loads]);
  return <Box ref={mountRef} sx={{ width: '100%', height: '100%' }} />;
};

export default Custom3dContainer;
